"use client";
import { GET_RESULT, GET_INFO } from "@/api";
import { RightIcon } from "@/assets/icons";
import { Button } from "@/components/Button";
import Dropdown from "@/components/Dropdown";
import Input from "@/components/Input";
import Tab from "@/components/Tab";
import TabLine from "@/components/TabLine";
import queryGenerator from "@/utils/queryGenerator";
import React, { useEffect, useRef, useState } from "react";
import CodeServer from "../code-server";
import Graph from "../graph";
import { toast } from "react-toastify";
import { useTheme } from "next-themes";

const bgpItems = ["Code Server", "Graph  Bgp Routes"];
const items = ["Code Server"];
const None = "ICMP Echo"

export default function RunTest() {
  const [region, setRegion] = useState("");
  const [network, setNetwork] = useState("");
  const [ip, setIp] = useState("");
  const [port, setPort] = useState("");
  const [protocol, setProtocol] = useState(None)
  const [query, setQuery] = useState("bgp");
  const [result, setResult] = useState({});
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [mode, setMode] = useState("Code Server");
  const [list, setList] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [networkList, setNetworkList] = useState([]);
  const [placeholder, setPlaceholder] = useState("");
  const { theme } = useTheme();
  const formRef = useRef(null);
  useEffect(() => {
    GET_INFO()
      .then(({ data }) => {
        // Set the list for the current query (bgp, ping, or traceroute)
        setList(data);

        // Set the region list (Tehran, Shiraz, etc.)
        setRegionList(Object.keys(data[query]));

        // Set the initial region (e.g., "Tehran")
        const initialRegion = Object.keys(data[query])[0];
        setRegion(initialRegion);

        // Set the network list for the initial region
        const networkList = data[query][initialRegion].map(
          (obj) => Object.keys(obj)[0]
        );
        setNetworkList(networkList);

        // Set the initial network with the desired structure
        const firstNetwork = data[query][initialRegion][0];
        const title = Object.keys(firstNetwork)[0];
        setNetwork(title);
        setPlaceholder(firstNetwork[title]);
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
      })
      .finally(() => {
        setListLoading(false);
      });
  }, []);

  useEffect(() => {
    if (loading) {
      scrollToBottom();
    }
  }, [loading]);

  useEffect(() => {
    if (list?.[query]) {
      // Set the region list (Tehran, Shiraz, etc.)
      setRegionList(Object.keys(list[query]));

      // Set the initial region (e.g., "Tehran")
      const initialRegion = Object.keys(list[query])[0];
      setRegion(initialRegion);

      // Set the network list for the initial region
      const networkList = list[query][initialRegion].map(
        (obj) => Object.keys(obj)[0]
      );
      setNetworkList(networkList);

      // Set the initial network with the desired structure
      const firstNetwork = list[query][initialRegion][0];
      const title = Object.keys(firstNetwork)[0];
      setNetwork(title);
      setPlaceholder(firstNetwork[title]);
    }
  }, [query]);

  useEffect(() => {
    if (query && region && list) {
      // Create an array of network names (keys)
      const networkList = list[query][region].map((obj) => Object.keys(obj)[0]);
      setNetworkList(networkList);

      // Set the first network object as the selected network
      if (list[query][region].length > 0) {
        const firstNetwork = list[query][region][0];
        const networkName = Object.keys(firstNetwork)[0];
        setNetwork(networkName);

        // Set the placeholder
        setPlaceholder(firstNetwork[networkName]);
      }
    }
  }, [query, region]);

  useEffect(() => {
    // Clear port when protocol is NONE
    if (protocol === None) {
      setPort('');
    }
  }, [protocol]);

  const scrollToBottom = () => {
    const buttonRef = document?.querySelector('#run-test-button');
    if (buttonRef) {
      buttonRef.scrollIntoView({ behavior: 'smooth' });
    }

  };

  const handleKeyDown = (event) => {
  if (event.key !== "Enter") return;

  const isButtonDisabled =
    !ip || !region || !network || loading ||
    (["ping", "traceroute"].includes(query) &&
      protocol !== None &&
      !isPortValid(port));

  if (isButtonDisabled) {
    event.preventDefault();
    return;
  }

  event.preventDefault();
  infoHandler();
  };

  const infoHandler = () => {
    const dataQuery = {
      address: ip,
      region: region,
      command: query,
      router: network,
    };

    // Add protocol and port to the query for ping and traceroute
    if (["ping", "traceroute"].includes(query)) {
      // Add protocol in lowercase to the query
      if (protocol !== None) {
        dataQuery.protocol = protocol.toLowerCase();

        if (port && protocol !== None) {
          dataQuery.port = port;
        }
        if (!port && protocol !== None) {
          toast.error('Add port between 1 and 65535')
        }
      }
      // Don't send port when protocol is NONE
    }

    setMode("Code Server");
    setLoading(true);
    setResult({});

    // Determine if we should use streaming (ping and traceroute operations)
    const shouldStream = ["ping", "traceroute"].includes(query);

    if (shouldStream) {
      const streamResults = [];

      // Set initial state to empty for a fresh start
      setResult({
        raw: '',
        data: [],
        command: query
      });

      GET_RESULT(queryGenerator(dataQuery), {
        stream: true,
        onOpen: () => {
        },
        onMessage: (data) => {
          // Get just the new line from the data object
          let newLine = '';
          if (data.raw) {
            newLine = data.raw;
          } else {
            try {
              newLine = JSON.stringify(data);
            } catch (e) {
              newLine = String(data);
            }
          }

          // Update right away with each new line
          setResult((prev) => {
            // Only append non-empty lines
            if (!newLine.trim()) return prev;

            // Append the new line to existing raw content
            const updatedRaw = prev.raw ? `${prev.raw}\n${newLine}` : newLine;

            return {
              ...prev,
              raw: updatedRaw,
              command: query
            };
          });

          // Store in array for later use
          streamResults.push(data);
        },
        onComplete: (allResults) => {
          setLoading(false);

          // Just mark as complete, preserving the incrementally built data
          setResult(prev => ({
            ...prev,
            complete: true
          }));
        },
        onError: (error) => {
          console.error("SSE error:", error);
          toast.error(
            "Error receiving stream data. Please try again.",
            {
              theme: theme === "dark" ? "dark" : "light",
            }
          );
          setLoading(false);
        }
      });
    } else {
      // Regular non-streaming request (for bgp)
      GET_RESULT(queryGenerator(dataQuery))
        .then((res) => {
          setResult(res.data);
        })
        .catch((err) => {
          console.error("Request error:", err);
          toast.error(
            err?.response?.data?.detail || "Error, please try again later!",
            {
              theme: theme === "dark" ? "dark" : "light",
            }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const validatePort = (value) => {
    // Don't validate if protocol is NONE
    if (protocol === None) return true;

    // For the basic validation during typing, allow any value
    if (value === '') return true;

    // Just check if it's a valid number (for typing purposes)
    const portNumber = parseInt(value, 10);
    return !isNaN(portNumber) && String(portNumber) === value;
  };

  // Function used specifically for button enable/disable
  const isPortValid = (value) => {
    // Don't validate if protocol is NONE
    if (protocol === None) return true;

    // Empty value is NOT valid when protocol is not None
    if (value === '') return false;

    // Ensure it's a valid integer between 1 and 65535
    const portNumber = parseInt(value, 10);
    return !isNaN(portNumber) && portNumber >= 1 && portNumber <= 65535 && String(portNumber) === value;
  };
  return (
    <>
      <section className=" lg:container mb-[110px] lg:mb-[158px]">
        <div
          ref={formRef}
          className=" relative dark:bg-dark-surface1 bg-surface1 rounded-xl p-4 lg:p-14 lg:pt-[72px] max-lg:py-16"
        >
          <div className="flex max-lg:px-4 justify-center absolute -top-0 -translate-y-2/4 w-full left-0 right-0">
            <Tab setState={setQuery} />
          </div>
          <div className="flex gap-6 max-lg:flex-col">
            <Dropdown
              title={query === 'bgp' ? 'Country' : 'Region'}
              state={region}
              setState={setRegion}
              items={regionList}
              loading={listLoading}
            />
            <Dropdown
              title="Data Center"
              state={network}
              setState={setNetwork}
              items={networkList}
              loading={listLoading}
            />
          </div>
          <div className="mt-6">
            <Input
              title="IP Address or Prefix"
              placeholder={placeholder}
              state={ip}
              setState={setIp}
              onKeyDown={handleKeyDown}
            />
          </div>
          {["ping", "traceroute"].includes(query) && (
            <div className="mt-6 flex gap-6 max-lg:flex-col">
              <Dropdown title="Protocol" items={[None, 'TCP', 'UDP']} state={protocol} setState={setProtocol} />
              <Input
                title="Port"
                disabled={protocol === None}
                placeholder="1 - 65535"
                state={port}
                setState={setPort}
                onKeyDown={handleKeyDown}
                validation={validatePort}
              />
            </div>
          )}
          <div className="flex justify-center absolute bottom-0 translate-y-2/4 w-full left-0 right-0">
            <Button
              id="run-test-button"
              className="h-10 w-36"
              isDisabled={
                !ip || !region || !network || loading ||
                (["ping", "traceroute"].includes(query) &&
                  protocol !== None &&
                  !isPortValid(port))
              }
              isLoading={loading}
              onClick={infoHandler}
              variant="primary"
            >
              Run Test
              <RightIcon />
            </Button>
          </div>
        </div>
        {result["raw"] || loading ? (
          <div className=" animate-fade-in">
            <TabLine
              state={mode}
              setState={setMode}
              loading={loading}
              items={result["AS_PATH"] ? bgpItems : items}
            />
            <section className=" container mt-2 py-12 px-6 gap-[10px] rounded-lg dark:bg-dark-surface1 bg-surface1 ">
              {mode === "Code Server" ? (
                <CodeServer result={result} loading={loading} />
              ) : (
                <Graph items={result["AS_PATH"] || []} />
              )}
            </section>
          </div>
        ) : (
          ""
        )}
      </section>
    </>
  );
}
